/* eslint-disable no-case-declarations */
import React, { createContext, useReducer } from 'react';
import PropTypes from 'prop-types';
import useQuery from 'hooks/useQuery';
import useDebounce from 'hooks/useDebounce';
import mParking from 'models/cpm/parking';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import useSearchParams from '../hooks/useSearchParams';

import { createFiltersReducer, createSearch, createSort } from '../utils/filters';

const ParkingsContext = createContext();

function formatDateForServer(dateLocale, { hours, minutes, seconds }) {
  if (!dateLocale) return null;
  const newDate = new Date(dateLocale);
  newDate.setHours(hours);
  newDate.setMinutes(minutes);
  newDate.setSeconds(seconds);
  return newDate.toISOString();
}

const DEFAULT_FILTERS = {
  search: {
    type: 'string',
    values: null,
    operator: 'ilike',
    formatter: (x) => x,
    filtersApiNames: ['licensePlate', 'reference', 'email', 'firstname', 'lastname'],
    displayUrl: true,
  },
  status: {
    type: 'string',
    values: null,
    fetchTrigger: null,
    sort: 'ASC',
    operator: 'eq',
    formatter: (x) => x,
    filtersApiNames: ['status'],
    displayUrl: true,
  },
  carpark: {
    type: 'string',
    values: null,
    operator: 'eq',
    formatter: (x) => x,
    displayUrl: true,
  },
  functionalType: {
    type: 'string',
    values: null,
    fetchTrigger: null,
    operator: 'eq',
    formatter: (x) => x,
    filtersApiNames: ['functionalType'],
    displayUrl: true,
  },
  beginFrom: {
    type: 'date',
    values: [new Date('2021-04-01')], // Before this date some parkings have not a reference and that could crash the server
    sort: 'DESC',
    operator: 'gteq',
    formatter: (date) => formatDateForServer(date, { hours: 0, minutes: 0, seconds: 0 }),
    filtersApiNames: ['begin'],
    displayUrl: true,
  },
  beginTo: {
    type: 'date',
    values: null,
    sort: 'DESC',
    operator: 'lteq',
    formatter: (date) => formatDateForServer(date, { hours: 23, minutes: 59, seconds: 59 }),
    filtersApiNames: ['begin'],
    displayUrl: true,
  },
  active: {
    type: 'boolean',
    values: [true],
    operator: 'eq',
    formatter: (x) => x,
    filtersApiNames: ['active'],
    displayUrl: false,
  },
};

function ParkingsProvider({ children }) {
  const token = useSelector((state) => _get(state, 'auth.token', null));

  /* 🔍 Filters */
  /* ---------- */

  const [filters, dispatchFilters] = useReducer(createFiltersReducer(DEFAULT_FILTERS, () => {}), DEFAULT_FILTERS);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);

  // 🌍 We first update filters with url search params at page init then filters update search params while using them
  useSearchParams({
    filters,
    dispatchFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  // We add a 🤏 debounce on the search input to avoid triggering too many requests
  // when the user is typing
  const DEBOUNCE_DELAY = 500;
  const searchDebounced = useDebounce(_get(filters, 'search.values[0]', null), DEBOUNCE_DELAY);
  const carparkId = _get(filters, 'carpark.values[0]', null);
  const variables = {
    input: {
      ...(carparkId ? { carparkId } : null),
    },
    search: createSearch(filters),
    sort: createSort(filters),
    pagination: {
      page_number: page,
      page_size: pageSize,
    },
  };

  const { data, /*  error, */ loading: parkingsLoading } = useQuery({
    query: mParking.queListParkingsAnyType,
    token,
    variables,
    dependencies: [
      // Automatic refetch
      _get(filters, 'status.fetchTrigger'),
      _get(filters, 'status.sort'),
      _get(filters, 'functionalType.fetchTrigger'),
      _get(filters, 'beginFrom.values[0]'),
      _get(filters, 'beginFrom.sort'),
      _get(filters, 'beginTo.values[0]'),
      _get(filters, 'beginTo.sort'),
      _get(filters, 'carpark.values[0]'),
      searchDebounced,
      // Paginations changes
      page,
      pageSize,
    ],
    // Fetch only if search params have been parsed from url
    // canFetch: searchParams,
  });

  return (
    <ParkingsContext.Provider
      value={{
        parkings: _get(data, 'listParkingsAnyType.entries', null),
        parkingsLoading,

        pageNumber: _get(data, 'listParkingsAnyType.pageNumber', null),
        pageSize: _get(data, 'listParkingsAnyType.pageSize', null),
        totalEntries: _get(data, 'listParkingsAnyType.totalEntries', null),
        totalPages: _get(data, 'listParkingsAnyType.totalPages', null),

        filters,
        dispatchFilters,

        setPage,
        setPageSize,
      }}
    >
      {children}
    </ParkingsContext.Provider>
  );
}
ParkingsProvider.propTypes = {
  children: PropTypes.node,
};
ParkingsProvider.defaultProps = {
  children: null,
};

export { ParkingsContext, ParkingsProvider };



// WEBPACK FOOTER //
// ./routes/cpm/routes/parkings/contexts/ParkingsContext.js